/**
 * Settings Registry - Single Source of Truth
 *
 * This file defines all settings pages in one place. All other files that need
 * settings page information should import from here.
 *
 * To add a new settings page:
 * 1. Add an entry to SETTINGS_PAGES below
 * 2. Create the page component in renderer/pages/settings/
 * 3. Add to SETTINGS_PAGE_COMPONENTS in renderer/pages/settings/settings-pages.ts
 * 4. Add icon to SETTINGS_ICONS in renderer/components/icons/SettingsIcons.tsx
 *
 * That's it - types, routes, and validation are derived automatically.
 */

import { t } from '@okclawpro/i18n'

/**
 * Settings page definition
 */
export interface SettingsPageDefinition {
  /** Unique identifier used in routes and navigation */
  id: string
  /** Display label in settings navigator */
  label: string
  /** Short description shown in settings navigator */
  description: string
}

/**
 * Settings page IDs — static array used for type derivation and runtime validation.
 * Order here determines display order in the settings navigator.
 */
const SETTINGS_PAGE_IDS = [
  'account', 'app', 'subscription', 'invoice', 'ai', 'appearance', 'input', 'workspace',
  'context', 'permissions', 'labels', 'pets', 'tools', 'server', 'sync', 'shortcuts', 'about-okclaw',
] as const

/**
 * Get the canonical list of all settings pages with translated labels/descriptions.
 * Must be called at runtime (after i18n is initialized) so t() resolves correctly.
 */
export function getSettingsPages(): SettingsPageDefinition[] {
  return [
    { id: 'account', label: t('settings.account'), description: t('settings.account.description') },
    { id: 'app', label: t('settings.app'), description: t('settings.app.description') },
    { id: 'subscription', label: t('settings.subscription'), description: t('settings.subscription.description') },
    { id: 'invoice', label: t('settings.invoice'), description: t('settings.invoice.description') },
    { id: 'ai', label: t('settings.ai'), description: t('settings.ai.description') },
    { id: 'appearance', label: t('settings.appearance'), description: t('settings.appearance.description') },
    { id: 'input', label: t('settings.input'), description: t('settings.input.description') },
    { id: 'workspace', label: t('settings.workspace'), description: t('settings.workspace.description') },
    { id: 'context', label: t('settings.context'), description: t('settings.context.description') },
    { id: 'permissions', label: t('settings.permissions'), description: t('settings.permissions.description') },
    { id: 'labels', label: t('settings.labels'), description: t('settings.labels.description') },
    { id: 'pets', label: t('settings.pets'), description: t('settings.pets.description') },
    { id: 'tools', label: t('settings.tools'), description: t('settings.tools.description') },
    { id: 'server', label: t('settings.server'), description: t('settings.server.description') },
    { id: 'sync', label: t('settings.sync'), description: t('settings.sync.description') },
    { id: 'shortcuts', label: t('settings.shortcuts'), description: t('settings.shortcuts.description') },
    { id: 'about-okclaw', label: t('settings.aboutOKClaw'), description: t('settings.aboutOKClaw.description') },
  ]
}

/**
 * @deprecated Use getSettingsPages() for translated labels.
 * Kept for backward compatibility with code that reads SETTINGS_PAGES statically.
 */
export const SETTINGS_PAGES = [
  { id: 'account', label: 'Account', description: 'Profile, balance, and security' },
  { id: 'app', label: 'App', description: 'Notifications and updates' },
  { id: 'subscription', label: 'Subscription', description: 'Plans, balance, and API keys' },
  { id: 'invoice', label: 'Invoice management', description: 'Applications, titles, files, and materials' },
  { id: 'ai', label: 'AI', description: 'Model, thinking, connections' },
  { id: 'appearance', label: 'Appearance', description: 'Theme, font, tool icons' },
  { id: 'input', label: 'Input', description: 'Send key, spell check' },
  { id: 'workspace', label: 'Workspace', description: 'Name, icon, working directory' },
  { id: 'context', label: 'Memory & Docs', description: 'User preferences, memory, and document sets' },
  { id: 'permissions', label: 'Permissions', description: 'Explore mode rules' },
  { id: 'labels', label: 'Labels', description: 'Manage session labels' },
  { id: 'pets', label: 'Pets', description: 'Desktop companion and status bubble' },
  { id: 'tools', label: 'Tools', description: 'External integrations and tools' },
  { id: 'server', label: 'Server', description: 'Remote server access' },
  { id: 'sync', label: 'Sync', description: 'WebDAV sync, backup, and restore' },
  { id: 'shortcuts', label: 'Shortcuts', description: 'Keyboard shortcuts' },
  { id: 'about-okclaw', label: 'About OK Work', description: 'About OK Work' },
] as const satisfies readonly SettingsPageDefinition[]

/**
 * Settings subpage type - derived from SETTINGS_PAGES
 * This replaces the manual union type in types.ts
 */
export type SettingsSubpage = (typeof SETTINGS_PAGES)[number]['id']

/**
 * Array of valid settings subpage IDs - for runtime validation
 */
export const VALID_SETTINGS_SUBPAGES: readonly SettingsSubpage[] = SETTINGS_PAGES.map(p => p.id)

/**
 * Type guard to check if a string is a valid settings subpage
 */
export function isValidSettingsSubpage(value: string): value is SettingsSubpage {
  return VALID_SETTINGS_SUBPAGES.includes(value as SettingsSubpage)
}

/**
 * Get settings page definition by ID (with translated label/description)
 */
export function getSettingsPage(id: SettingsSubpage): SettingsPageDefinition {
  const page = getSettingsPages().find(p => p.id === id)
  if (!page) throw new Error(`Unknown settings page: ${id}`)
  return page
}
